import { ExtendedBooking } from '@wix/ambassador-bookings-reader-v2-extended-booking/types';
import { ServiceType } from '@wix/ambassador-bookings-services-v2-service/types';
import { DateTimeFormatter } from '@wix/bookings-date-time';
import { SlotService } from '../state/types';

export function formatBookingTime(
  slotService: SlotService,
  existingBookingDetails: ExtendedBooking,
  dateRegionalSettingsLocale: string,
) {
  const timezone =
    slotService.service.type === ServiceType.CLASS
      ? slotService.nestedSlot.timezone
      : undefined;

  const classStartTime =
    existingBookingDetails.booking?.bookedEntity?.slot?.startDate;
  const courseStartTime =
    existingBookingDetails.booking?.bookedEntity?.schedule?.firstSessionStart!;

  const timeFormatter = new DateTimeFormatter(
    dateRegionalSettingsLocale,
    timezone,
  );

  const startTime = timeFormatter.formatTime(classStartTime || courseStartTime);
  return startTime;
}

export function areDatesEqual(date1: string, date2: string) {
  return new Date(date1).getTime() === new Date(date2).getTime();
}

export function normalizeToUtc(date: string): string {
  const trimmed = date.trim();
  return hasTimezoneSuffix(trimmed) ? trimmed : `${trimmed}Z`;
}

export function hasTimezoneSuffix(date: string) {
  return /(?:[Zz]|[+-]\d{2}:?\d{2})$/.test(date);
}

export function toLocalDateTimeString(date: string, timeZone?: string): string {
  if (!timeZone || !hasTimezoneSuffix(date)) {
    return date;
  }

  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(new Date(date))
      .map(({ type, value }) => [type, value]),
  );

  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`;
}

export type LocalDateTimeParts = {
  year: number;
  monthOfYear: number;
  dayOfMonth: number;
  hourOfDay?: number;
  minutesOfHour?: number;
  secondsOfMinute?: number;
};

export function convertToTimeZone(date: string, timeZone: string): Date {
  const locallyParsedDate = new Date(date);

  return convertLocalDateTimeToTimeZone(
    {
      year: locallyParsedDate.getFullYear(),
      monthOfYear: locallyParsedDate.getMonth() + 1,
      dayOfMonth: locallyParsedDate.getDate(),
      hourOfDay: locallyParsedDate.getHours(),
      minutesOfHour: locallyParsedDate.getMinutes(),
      secondsOfMinute: locallyParsedDate.getSeconds(),
    },
    timeZone,
  );
}

export function convertLocalDateTimeToTimeZone(
  {
    year,
    monthOfYear,
    dayOfMonth,
    hourOfDay = 0,
    minutesOfHour = 0,
    secondsOfMinute = 0,
  }: LocalDateTimeParts,
  timeZone: string,
): Date {
  const utcDate = new Date(
    Date.UTC(
      year,
      monthOfYear - 1,
      dayOfMonth,
      hourOfDay,
      minutesOfHour,
      secondsOfMinute,
    ),
  );

  return new Date(utcDate.getTime() - getTimeZoneOffset(utcDate, timeZone));
}

export function isPastDate(
  date: string,
  timezone?: string,
  now: Date = new Date(),
): boolean {
  const comparable =
    timezone && !hasTimezoneSuffix(date)
      ? convertToTimeZone(date, timezone)
      : new Date(date);
  return comparable.getTime() < now.getTime();
}

function getTimeZoneOffset(date: Date, timeZone: string) {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hourCycle: 'h23',
    });
    const parts = Object.fromEntries(
      formatter
        .formatToParts(date)
        .filter(({ type }) => type !== 'literal')
        .map(({ type, value }) => [type, Number(value)]),
    );
    const formattedDateAsUtc = Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second,
    );

    return formattedDateAsUtc - date.getTime();
  } catch (e) {
    if (e instanceof RangeError) {
      return 0;
    }
    throw e;
  }
}
